import { openPaymentPage } from "@/lib/open-checkout";
import { checkoutAttribution } from "@/lib/checkout-attribution";
import { localeOrigin } from "@/lib/locale";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { track } from "@/lib/track";
import { trackFunnel } from "@/lib/funnel";
import { markProviderRedirect } from "@/lib/checkout-return";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, CreditCard, Check } from "lucide-react";
import { CheckoutStatusBanner } from "@/components/CheckoutStatusBanner";
import { buildDigitalConsent } from "@/lib/digital-consent";
import { DigitalConsentCheckbox } from "@/components/DigitalConsentCheckbox";
import {
  startGuestStripeCheckout,
  startGuestPaypalCheckout,
} from "@/lib/guest-checkout.functions";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const Route = createFileRoute("/checkout/guest")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Start your membership — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Start your Recovery Trauma™ membership for $14.99 per month. Pay securely with card or PayPal, then create your password. Cancel anytime.",
      },
      { property: "og:title", content: "Start your membership — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Pay securely with card or PayPal and get immediate access to your Recovery Space.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: DirectCheckoutPage,
});

function DirectCheckoutPage() {
  const stripeFn = useServerFn(startGuestStripeCheckout);
  const paypalFn = useServerFn(startGuestPaypalCheckout);

  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState<null | "stripe" | "paypal">(null);
  const [error, setError] = useState<string | null>(null);
  const [configBlocked, setConfigBlocked] = useState(false);
  // Express digital-content consent, captured before checkout opens.
  const [digitalConsent, setDigitalConsent] = useState(false);

  useEffect(() => {
    track("checkout_page_viewed", { variant: "direct_checkout" }, { source: "direct_checkout" });
    trackFunnel("membership_viewed", { source: "direct_checkout" }, { variant: "direct_checkout" });
    trackFunnel(
      "page_view",
      { source: "direct_checkout", dedupeKey: "checkout_guest_view" },
      { surface: "checkout_guest" },
    );
  }, []);

  /** Fires once, the first time a valid email address is committed. */
  function onEmailCommitted() {
    if (!EMAIL_RE.test(email.trim().toLowerCase())) return;
    trackFunnel("email_entered", {
      source: "direct_checkout",
      dedupeKey: "checkout_guest_email",
    });
  }

  async function start(provider: "stripe" | "paypal") {
    setError(null);
    const trimmed = email.trim().toLowerCase();
    trackFunnel("payment_method_selected", { source: "direct_checkout", provider });
    if (!EMAIL_RE.test(trimmed)) {
      setError("Please enter a valid email address.");
      track("checkout_error", { provider, reason: "invalid_email" }, { source: "direct_checkout" });
      return;
    }
    onEmailCommitted();
    if (!digitalConsent) {
      setError("Please tick the box to confirm you want immediate access before paying.");
      return;
    }
    setBusy(provider);
    track("join_clicked", { plan: "membership_monthly", provider, flow: "direct" }, { source: "direct_checkout" });
    trackFunnel("join_clicked", { source: "direct_checkout", provider });
    trackFunnel("payment_started", { source: "direct_checkout", provider });
    track(
      provider === "stripe" ? "checkout_stripe_clicked" : "checkout_paypal_clicked",
      { plan: "membership_monthly", flow: "direct" },
      { source: "direct_checkout" },
    );
    try {
      const origin = localeOrigin();
      const res =
        provider === "stripe"
          ? await stripeFn({
              data: { email: trimmed, origin, attribution: checkoutAttribution(), digital_consent: buildDigitalConsent() },
            })
          : await paypalFn({ data: { email: trimmed, origin, attribution: checkoutAttribution(), digital_consent: buildDigitalConsent() } });

      if (!res.ok) {
        setConfigBlocked(
          res.reason === "not_configured" || res.reason === "stripe_error",
        );
        setError(res.message);
        track("checkout_blocked", { provider, reason: res.reason }, { source: "direct_checkout" });
        setBusy(null);
        return;
      }
      track(
        "checkout_opened",
        { plan: "membership_monthly", provider, flow: "direct" },
        { source: "direct_checkout" },
      );
      trackFunnel("checkout_opened", { source: "direct_checkout", provider });
      trackFunnel("redirect_started", { source: "direct_checkout", provider });
      markProviderRedirect("membership", provider);
      openPaymentPage(res.url);
    } catch {
      setError("Something went wrong opening checkout. Please try again.");
      setBusy(null);
    }
  }

  return (
    <main className="min-h-dvh bg-gradient-to-b from-[oklch(0.985_0.012_85)] via-background to-[oklch(0.96_0.03_82)] px-4 py-10 sm:py-16">
      <div className="mx-auto w-full max-w-lg">
        <h1 className="text-center font-display text-2xl text-foreground sm:text-3xl">
          Start your Recovery Trauma™ Membership
        </h1>
        <div className="mt-3 flex items-baseline justify-center gap-2">
          <span className="font-serif text-4xl text-foreground sm:text-5xl">$14.99</span>
          <span className="text-base text-muted-foreground">/ month</span>
        </div>
        <p className="mt-1 text-center text-sm text-muted-foreground">
          $14.99 USD/month · Monthly subscription · Cancel anytime
        </p>

        <div className="mt-6">
          <CheckoutStatusBanner forceDegraded={configBlocked} />
        </div>

        <div className="mt-1 rounded-[1.75rem] border border-gold/30 bg-card p-5 shadow-[var(--shadow-gold)] sm:p-7">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="checkout-email">Your email address</Label>
              <Input
                id="checkout-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={onEmailCommitted}
              />
              <p className="text-xs text-muted-foreground">
                We use this to set up your membership. If you already have an account, your
                payment is linked to it — no duplicate accounts.
              </p>
            </div>
            <div className="rounded-xl border border-gold/25 bg-cream/50 p-3 dark:bg-cream/5">
              <DigitalConsentCheckbox checked={digitalConsent} onChange={setDigitalConsent} id="digital-consent-guest" />
            </div>
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}

            <Button
              type="button"
              className="w-full py-6 text-base"
              disabled={busy !== null || !digitalConsent}
              onClick={() => start("stripe")}
            >
              {busy === "stripe" ? (
                <Loader2 className="me-2 h-5 w-5 animate-spin" />
              ) : (
                <CreditCard className="me-2 h-5 w-5" />
              )}
              Pay by Card
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full py-6 text-base"
              disabled={busy !== null || !digitalConsent}
              onClick={() => start("paypal")}
            >
              {busy === "paypal" ? <Loader2 className="me-2 h-5 w-5 animate-spin" /> : null}
              Pay with PayPal
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              Secure payment processed by Stripe or PayPal.
            </p>
          </div>

          <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
            {[
              "You choose your password straight after paying.",
              "Cancel anytime — no contract.",
            ].map((line) => (

              <li key={line} className="flex items-start gap-2">
                <Check className="mt-0.5 h-4 w-4 flex-none text-[var(--gold-deep)]" />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <p className="mt-5 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4" />
            Recovery Trauma™ never sees or stores your card details.
          </p>
        </div>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already a member?{" "}
          <Link to="/login" className="font-medium text-[var(--gold-deep)] hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </main>
  );
}
